// src/mobile/MobilePhotoCapture.js
// CORREGIDO: El input type="file" ahora se renderiza persistentemente para evitar que fileInputRef.current sea null.
// Eliminados los console.log de depuración.
// +++ INICIO: COMPRESIÓN DE IMÁGENES Y MANEJO DE ERRORES +++
// Se añade la biblioteca 'browser-image-compression' para reducir el tamaño de las imágenes antes de subirlas.
// Se añade lógica para manejar el error 413 (Payload Too Large) del servidor con un componente <Alert>.
// Se redirige a una página de éxito en lugar de a 'about:blank'.
// Se añade botón y lógica para cancelar la sesión de forma condicional.
// Se optimiza la carga inicial usando un endpoint de metadatos.
// Se mejora la lógica de subida para evitar duplicados en caso de fallo parcial.
// +++ FIN: COMPRESIÓN DE IMÁGENES Y MANEJO DE ERRORES +++
// +++ INICIO: MEJORAS UX LÍMITES DE FOTOS (v11) +++
// Se homologa el comportamiento de los botones "Usar Cámara" y "Añadir desde Galería".
// Ambos ahora verifican el límite de fotos ANTES de ejecutar la acción.
// +++ FIN: MEJORAS UX LÍMITES DE FOTOS (v11) +++

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spin, Typography, Progress, message, Alert, List, Image, Tooltip, Divider, Popconfirm } from 'antd';
import {
  CameraOutlined, CheckCircleOutlined, CloseCircleOutlined,
  PictureOutlined, UploadOutlined, DeleteOutlined, EyeOutlined,
  StopOutlined
} from '@ant-design/icons';
import moment from 'moment';
import './MobilePhotoCapture.css';
// --- AJUSTE API: Se importa el servicio unificado
import { deliveryService } from '../services/api';
// --- Se importa la biblioteca de compresión de imágenes ---
import imageCompression from 'browser-image-compression';

const { Title, Text } = Typography;

// --- CONFIGURACIÓN OPTIMIZADA PARA HANDHELDS ---
// Se mantiene el nombre de variable original pero con los parámetros seguros
const AGGRESSIVE_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1024, // Bajado a 1024px para evitar colapso de RAM (OOM)
  useWebWorker: true,
  maxIteration: 4,        // Bajado a 4 intentos para evitar bloqueos
  initialQuality: 0.6,    // Calidad media para acertar rápido
  fileType: 'image/jpeg'  // Forzamos JPEG para estandarizar y aligerar
};

const POST_COMPRESSION_LIMIT_MB = 1.0;

// --- HELPER NUEVO: Protección contra congelamiento ---
const compressWithTimeout = (file, options, timeoutMs = 15000) => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("TIMEOUT_COMPRESSION"));
    }, timeoutMs);

    imageCompression(file, options)
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

const MobilePhotoCapture = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionStatus, setSessionStatus] = useState({
    docEntry: null,
    expiresAt: null,
    photosCount: 0,
    maxPhotosLimit: 30, // Valor por defecto mientras carga
  });
  const [cameraActive, setCameraActive] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // --- CAMBIO: Estado del spinner mejorado para mostrar texto dinámico ---
  const [isCompressing, setIsCompressing] = useState(false);
  const [processingMsg, setProcessingMsg] = useState("Comprimiendo imágenes...");
  // ----------------------------------------------------------------------

  const [currentUpload, setCurrentUpload] = useState({ index: 0, total: 0 });
  const [uploadAlert, setUploadAlert] = useState(null);
  const [limitAlert, setLimitAlert] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const alertRef = useRef(null);

  const checkSession = useCallback(async () => {
    if (!sessionId) { setError('ID de sesión no proporcionado.'); setLoading(false); return; }
    try {
      const response = await deliveryService.getSessionMetadata(sessionId);
      if (response.data.success) {
        const { docEntry, expiresAt, photosCount, maxPhotosLimit, status } = response.data.data;
        setSessionStatus({ docEntry, expiresAt, photosCount, maxPhotosLimit });
        const now = new Date();
        if (expiresAt && new Date(expiresAt) < now) {
          setError("La sesión ha expirado.");
        } else if (status === 'photos_completed') {
          setError("Esta sesión de fotos ya fue completada.");
        } else if (status === 'expired') {
          setError("La sesión ha expirado.");
        }
      } else {
        setError(response.data.message || 'Error al verificar estado de sesión');
      }
    } catch (err) {
      console.error('Error al verificar sesión:', err);
      if (err.response?.status === 404) {
        setError('Sesión no encontrada o inválida.');
      } else {
        setError('Error de comunicación con el servidor al verificar sesión.');
      }
    } finally {
      if (loading) setLoading(false);
    }
  }, [sessionId, loading]);

  useEffect(() => {
    if (sessionId) {
      checkSession();
    } else {
      setError('ID de sesión no válido en la URL.');
      setLoading(false);
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if ((uploadAlert || limitAlert) && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [uploadAlert, limitAlert]);

  const isLimitReached = sessionStatus.photosCount + selectedImages.length >= sessionStatus.maxPhotosLimit;

  const startCamera = async () => {
    setError(null);
    setUploadAlert(null);
    setLimitAlert(null);

    if (isLimitReached) {
      setLimitAlert({ type: 'warning', message: `Has alcanzado el máximo de ${sessionStatus.maxPhotosLimit} fotos para este listado. ¡Ya tienes todo listo para subir tus evidencias!` });
      //setLimitAlert({ type: 'warning', message: `Límite de ${sessionStatus.maxPhotosLimit} fotos alcanzado.` });
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { message.error("API de cámara no soportada."); console.error("getUserMedia no disponible."); setError("API de cámara no soportada."); return; }
    try { setCameraActive(true); const constraints = { video: { facingMode: { ideal: 'environment' } }, audio: false }; const stream = await navigator.mediaDevices.getUserMedia(constraints); streamRef.current = stream; if (videoRef.current) { videoRef.current.srcObject = stream; } } catch (err) { console.error('Error cámara:', err); if (err.name === 'NotAllowedError') { message.error('Permiso cámara denegado.'); setError('Permiso cámara denegado.'); } else if (err.name === 'NotFoundError') { message.error('No se encontró cámara.'); setError('No se encontró cámara.'); } else if (err.name === 'NotReadableError') { message.error('Cámara ocupada.'); setError('Cámara ocupada.'); } else { message.error('Error cámara. Verifique permisos/HTTPS.'); setError(`Error cámara: ${err.name}`); } setCameraActive(false); }
  };

  const handleOpenGallery = () => {
    setError(null);
    setUploadAlert(null);
    setLimitAlert(null);

    if (isLimitReached) {
      setLimitAlert({ type: 'warning', message: `Has alcanzado el máximo de ${sessionStatus.maxPhotosLimit} fotos para este listado. ¡Ya tienes todo listo para subir tus evidencias!` });
      return;
    }
    // Si no se ha alcanzado el límite, se abre el explorador de archivos
    fileInputRef.current?.click();
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current || !videoRef.current.srcObject) { message.error("Error preparando captura."); return; };
    const video = videoRef.current; const canvas = canvasRef.current;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    try {
      const context = canvas.getContext('2d'); if (!context) { throw new Error("No context"); }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(async (blob) => {
        if (!blob) {
          message.error("Error al crear blob de la imagen.");
          stopCamera();
          return;
        }
        // --- CAMBIO: Activamos spinner con mensaje ---
        setIsCompressing(true);
        setProcessingMsg("Procesando captura...");

        try {
          // --- CAMBIO: Usamos compressWithTimeout ---
          const compressedFile = await compressWithTimeout(blob, AGGRESSIVE_COMPRESSION_OPTIONS);

          const compressedSizeMB = compressedFile.size / 1024 / 1024;
          if (compressedSizeMB > POST_COMPRESSION_LIMIT_MB) {
            message.error(`La foto capturada no pudo ser comprimida lo suficiente (${compressedSizeMB.toFixed(2)}MB). Por favor, intente en un entorno con mejor iluminación.`);
            return;
          }

          const imageDataUrl = await imageCompression.getDataUrlFromFile(compressedFile);

          const newImage = { id: `capture-${Date.now()}`, dataUrl: imageDataUrl, name: `captura_${Date.now()}.jpg` };
          setSelectedImages(prev => [...prev, newImage]);
          message.success(`Imagen procesada (${compressedSizeMB.toFixed(2)}MB).`);
          stopCamera();
        } catch (compressionError) {
          if (compressionError.message === "TIMEOUT_COMPRESSION") {
            message.error("El dispositivo tardó demasiado en procesar la foto.");
          } else {
            message.error({ content: 'Error al comprimir la imagen.' });
            console.error("Error de compresión:", compressionError);
          }
          stopCamera();
        } finally {
          setIsCompressing(false);
          setProcessingMsg("Comprimiendo imágenes..."); // Reset del mensaje
        }
      }, 'image/jpeg', 0.9);
    } catch (err) { message.error("Error procesando imagen."); stopCamera(); }
  };

  const handleFileUpload = async (event) => {
    let files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length === 0) {
        if (fileInputRef.current) { fileInputRef.current.value = ""; }
        return;
    }
    
    // Limpieza de estados previos
    setError(null);
    setUploadAlert(null);
    setLimitAlert(null);

    const currentTotal = sessionStatus.photosCount + selectedImages.length;
    const availableSlots = sessionStatus.maxPhotosLimit - currentTotal;

    if (availableSlots <= 0) {
        setLimitAlert({ type: 'warning', message: `Límite de ${sessionStatus.maxPhotosLimit} fotos alcanzado. No se pueden agregar más.` });
        if (fileInputRef.current) { fileInputRef.current.value = ""; }
        return;
    }

    const originalSelectionCount = files.length;
    if (files.length > availableSlots) {
        files = files.slice(0, availableSlots);
        message.warning(`Solo se procesarán las primeras ${availableSlots} fotos para respetar el límite.`);
    }

    setIsCompressing(true);
    
    const newImages = [];
    
    // --- CONTADORES DE ERRORES ---
    let countFormatError = 0;
    let countSizeError = 0; // > 20MB
    let countTimeoutError = 0;
    let countInternalError = 0;

    let processedCount = 0;
    const totalToProcess = files.length;

    // Lista negra de extensiones que el navegador no soporta nativamente o dan problemas
    const formatosNoSoportados = ['tif', 'tiff', 'rw2', 'arw', 'cr2', 'nef', 'dng']; 

    try {
        // --- INICIO PROCESO SECUENCIAL ---
        for (const file of files) {
            processedCount++;
            setProcessingMsg(`Procesando foto ${processedCount} de ${totalToProcess}...`);

            // 1. Validación de TIPO y EXTENSIÓN
            const extension = file.name.split('.').pop().toLowerCase();
            
            if (!file.type.startsWith('image/') || formatosNoSoportados.includes(extension)) {
                console.warn(`Omitido por formato: ${file.name}`);
                countFormatError++;
                continue;
            }

            // 2. Validación de TAMAÑO (> 20MB)
            if (file.size > 20971520) { 
                console.warn(`Omitido por tamaño: ${file.name}`);
                countSizeError++;
                continue; 
            }

            try {
                // 3. Compresión con Timeout
                const compressedFile = await compressWithTimeout(file, AGGRESSIVE_COMPRESSION_OPTIONS);
                
                const compressedSizeMB = compressedFile.size / 1024 / 1024;
                if (compressedSizeMB > POST_COMPRESSION_LIMIT_MB) {
                    message.warning(`Una foto quedó pesada (${compressedSizeMB.toFixed(2)}MB), pero se agregó.`);
                }
                
                const dataUrl = await imageCompression.getDataUrlFromFile(compressedFile);
                
                newImages.push({ 
                    id: `gallery-${Date.now()}-${Math.random()}`, 
                    dataUrl: dataUrl, 
                    name: file.name 
                });

            } catch (err) {
                // 4. Clasificación de errores de proceso
                if (err.message === "TIMEOUT_COMPRESSION") {
                    countTimeoutError++;
                } else {
                    console.error(`Error comprimiendo ${file.name}:`, err);
                    countInternalError++;
                }
            }
        }
        // --- FIN PROCESO SECUENCIAL ---


        // --- CONSTRUCCIÓN DEL MENSAJE INTELIGENTE ---
        const totalSkipped = countFormatError + countSizeError + countTimeoutError + countInternalError;

        // 1. Preparamos la lista de razones
        const reasons = [];
        if (countSizeError > 0) reasons.push(`${countSizeError} por exceso de tamaño`);
        if (countFormatError > 0) reasons.push(`${countFormatError} por formato no válido`);
        if (countTimeoutError > 0) reasons.push(`${countTimeoutError} por lentitud del dispositivo`);
        if (countInternalError > 0) reasons.push(`${countInternalError} por error inesperado`);

        // 2. Definimos la gramática del detalle
        // Si reasons.length es 1 -> "se omitieron 3 por exceso de tamaño"
        // Si reasons.length > 1  -> "se omitieron 5: 3 por exceso..., 2 por formato..."
        const errorDetail = reasons.length === 1 
            ? `se omitieron ${reasons[0]}` 
            : `se omitieron ${totalSkipped}: ${reasons.join(', ')}`;

        // 3. Definimos la gramática capitalizada para inicio de frase (Caso de fallo total)
        const errorDetailCapitalized = reasons.length === 1
            ? `Se omitieron ${reasons[0]}`
            : `Se omitieron ${totalSkipped}: ${reasons.join(', ')}`;


        if (newImages.length > 0) {
            setSelectedImages(prev => [...prev, ...newImages]);
            
            if (totalSkipped > 0) {
                // Éxito parcial (Amarillo)
                setLimitAlert({ 
                    type: 'warning', 
                    message: `Se añadieron ${newImages.length} fotos, pero ${errorDetail}.` 
                });
            } else if (originalSelectionCount > newImages.length) {
                // Caso recorte por límite de cupo (Azul/Info)
                setLimitAlert({ 
                    type: 'info', 
                    message: `Se añadieron ${newImages.length} fotos exitosamente.` 
                });
            } else {
                // Éxito total (Verde/Toast)
                message.success(`${newImages.length} imágenes listas.`);
            }

        } else if (totalSkipped > 0) {
            // Fallo total (Rojo)
            setLimitAlert({ 
                type: 'error', 
                message: `No se pudo agregar ninguna foto. ${errorDetailCapitalized}.` 
            });
        }

    } catch (err) {
        console.error("Error global en carga:", err);
        message.error("Error inesperado al procesar archivos.");
    } finally {
         if (fileInputRef.current) {
             fileInputRef.current.value = "";
         }
         setIsCompressing(false);
         setProcessingMsg("Comprimiendo imágenes..."); 
    }
  };

  const removeSelectedImage = (idToRemove) => {
    setSelectedImages(prev => prev.filter(img => img.id !== idToRemove));
  };

  const uploadPhotos = async () => {
    if (selectedImages.length === 0 || !sessionId) return;
    setUploading(true);
    setUploadAlert(null);
    setLimitAlert(null);

    const imagesToUpload = [...selectedImages];
    const batchTotal = imagesToUpload.length;
    let successfulUploads = 0;

    for (const [index, img] of imagesToUpload.entries()) {
      setCurrentUpload({ index: index + 1, total: batchTotal });
      setUploadProgress(0);

      try {
        const deviceInfo = { userAgent: navigator.userAgent, time: new Date().toISOString(), screen: `${window.screen.width}x${window.screen.height}` };
        await deliveryService.addPhotoToSession(
          sessionId,
          { imageData: img.dataUrl, description: `Foto ${sessionStatus.photosCount + successfulUploads + 1}`, deviceInfo },
          (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
            } else {
              setUploadProgress(50);
            }
          }
        );
        setUploadProgress(100);
        successfulUploads++;
        setSelectedImages(prev => prev.filter(prevImg => prevImg.id !== img.id));

      } catch (err) {
        const backendMessage = err.response?.data?.message;
        const fallbackMessage = `Error al subir la foto "${img.name || `capturada ${index + 1}`}".`;
        const errorMessage = backendMessage || fallbackMessage;

        setUploadAlert(errorMessage);
        message.error(errorMessage);
        setUploading(false);
        checkSession();
        return;
      }
    }

    setUploading(false);

    if (successfulUploads === batchTotal && batchTotal > 0) {
      message.success(`${successfulUploads} imagen(es) subida(s) correctamente. Finalizando...`, 2);
      try {
        console.log("FOTOS SUBIDAS. [1] Intentando cerrar la sesión...");
        await deliveryService.closePhotoSession(sessionId);
        console.log("SESIÓN CERRADA. [2] Navegando a pantalla de éxito...");
        navigate('/capture-success');
      } catch (closeErr) {
        message.error("Fotos subidas, pero hubo un error al finalizar la sesión.");
        console.log("Fotos subidas, pero hubo un error al finalizar la sesión.");
        // Agregamos la navegación aquí también para que el usuario no se quede atascado
        navigate('/capture-success');
      }
    } else if (batchTotal > 0) {
      message.warning(`Proceso de subida incompleto.`, 5);
      checkSession();
    }
  };

  const handleCancelSession = async () => {
    setCancelling(true);
    try {
      await deliveryService.cancelPhotoSession(sessionId);
      message.success("Sesión cerrada.");
      navigate('/capture-success', { state: { cancelled: true } });
    } catch (err) {
      console.error("Error al cerrar la sesión:", err);
      message.error("No se pudo cerrar la sesión. Por favor, intente de nuevo.");
    } finally {
      setCancelling(false);
    }
  };

  const discardPhotos = () => {
    setSelectedImages([]);
    setUploadAlert(null);
    setLimitAlert(null);
  };

  // --- CAMBIO: Spinner recibe texto dinámico ---
  const FullscreenSpinner = ({ text }) => (
    <div className="fullscreen-spinner">
      <div style={{ textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '10px', color: '#fff', fontSize: '16px' }}>{text}</div>
      </div>
    </div>
  );

  if (loading) {
    return <FullscreenSpinner text="Verificando sesión..." />;
  }

  if (error) {
    return <div className="mobile-capture-container"><Card><Alert message="Error" description={error} type="error" showIcon /></Card></div>;
  }

  const now = new Date();
  const expiryDate = sessionStatus.expiresAt ? new Date(sessionStatus.expiresAt) : null;
  const isExpired = expiryDate && now > expiryDate;

  if (isExpired) {
    return (
      <div className="mobile-capture-container">
        <Card className="mobile-capture-card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <CloseCircleOutlined style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
            <Title level={4}>Sesión Expirada</Title>
            <Text>La sesión de captura ha expirado.</Text>
          </div>
        </Card>
      </div>
    );
  }

  // ... (todo el código anterior igual) ...

  const showInitialButtons = !cameraActive && selectedImages.length === 0;

  return (
    <div className="mobile-capture-container">
      <style>{`
        .fullscreen-spinner {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex; justify-content: center; align-items: center; z-index: 9999;
        }
      `}</style>

      {isCompressing && <FullscreenSpinner text={processingMsg} />}

      <input ref={fileInputRef} id="file-upload" type="file" accept="image/*" multiple onChange={handleFileUpload} style={{ display: 'none' }} />

      <Card className="mobile-capture-card" title={`Captura para Entrega ${sessionStatus.docEntry || '...'}`}>

        {/* 1. ZONA DE ALERTAS (Visible siempre que no estemos en modo cámara) */}
        {!cameraActive && (
          <div ref={alertRef} style={{ marginBottom: 16 }}>
            {uploadAlert && (
              <Alert
                message="Error"
                description={uploadAlert}
                type="error"
                showIcon
                closable
                onClose={() => setUploadAlert(null)}
              />
            )}
            {limitAlert && (
              <Alert
                message={limitAlert.type === 'error' ? 'Error de Carga' : (limitAlert.type === 'warning' ? 'Aviso' : 'Información')}
                description={limitAlert.message}
                type={limitAlert.type}
                showIcon
                closable
                onClose={() => setLimitAlert(null)}
                style={{ marginTop: uploadAlert ? '16px' : '0' }}
              />
            )}
          </div>
        )}

        {/* 2. MODO CÁMARA */}
        {cameraActive && (
          <div className="camera-container">
            <video ref={videoRef} autoPlay playsInline className="camera-video" />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="camera-actions">
              <Button type="primary" shape="circle" icon={<CameraOutlined />} onClick={capturePhoto} size="large" className="capture-button" />
              <Button danger onClick={stopCamera} size="small" style={{ marginTop: 8 }}> Cancelar Cámara </Button>
            </div>
          </div>
        )}

        {/* 3. MODO PREVISUALIZACIÓN (LO QUE MARCATE EN ROJO) */}
        {/* CONDICIÓN ESTRICTA: Solo si NO es cámara Y SI hay fotos (> 0) */}
        {(!cameraActive && selectedImages.length > 0) && (
          <div className="preview-container">

            <Title level={5} style={{ marginBottom: '10px' }}>Imágenes para Subir ({selectedImages.length})</Title>

            <Image.PreviewGroup>
              <List
                grid={{ gutter: 8, column: 3 }}
                dataSource={selectedImages}
                renderItem={(item) => (
                  <List.Item key={item.id}>
                    <Tooltip title={item.name} placement="bottom">
                      <Card
                        size="small"
                        hoverable
                        cover={
                          <Image
                            alt={item.name}
                            src={item.dataUrl}
                            style={{ height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                            preview={{ mask: <EyeOutlined /> }}
                          />
                        }
                        actions={[
                          <Tooltip title="Eliminar Selección">
                            <Button icon={<DeleteOutlined />} onClick={() => removeSelectedImage(item.id)} size="small" danger disabled={uploading} />
                          </Tooltip>
                        ]}
                      />
                    </Tooltip>
                  </List.Item>
                )}
              />
            </Image.PreviewGroup>

            {uploading ? (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Progress percent={uploadProgress} status="active" />
                <Text>Subiendo imagen {currentUpload.index} de {currentUpload.total}...</Text>
              </div>
            ) : (
              <div className="camera-actions">
                <Button type="primary" icon={<UploadOutlined />} onClick={uploadPhotos} block disabled={selectedImages.length === 0}> Subir {selectedImages.length} Foto(s) y Finalizar</Button>
                <Button danger icon={<CloseCircleOutlined />} onClick={discardPhotos} block style={{ marginTop: 8 }} disabled={selectedImages.length === 0}> Descartar Selección </Button>
                <Divider>O añadir más</Divider>
                <Button style={{ marginTop: 8 }} icon={<PictureOutlined />} onClick={handleOpenGallery} block> Añadir desde Galería </Button>
                <Button style={{ marginTop: 8 }} icon={<CameraOutlined />} onClick={startCamera} block> Usar Cámara </Button>
              </div>
            )}
          </div>
        )}

        {/* 4. BOTONES INICIALES (SOLO SI NO HAY FOTOS) */}
        {showInitialButtons && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Title level={5}>Añadir Evidencia Fotográfica</Title>
            <div style={{ marginTop: 20 }}>
              <Button type="primary" icon={<CameraOutlined />} onClick={startCamera} block> Usar Cámara </Button>
              <Button style={{ marginTop: 12 }} icon={<PictureOutlined />} onClick={handleOpenGallery} block> Seleccionar de Galería </Button>
            </div>
            <div style={{ marginTop: 20 }}>
              <Text type="secondary">Fotos ya guardadas: {sessionStatus.photosCount || 0}</Text>
              {expiryDate && (<div> <Text type="secondary"> Tiempo restante: {Math.max(0, Math.floor((expiryDate - now) / 60000))} min </Text> </div>)}
            </div>
          </div>
        )}

      </Card>
    </div>
  );
};

export default MobilePhotoCapture;